import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Typography,
    Paper,
    Button,
    TextField,
    Grid,
    IconButton,
    CircularProgress,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import { ArrowBack, Save } from "@mui/icons-material";
import { URLHandler } from "src/services/URLHandler";
import { useCreateApplication } from "src/api/client-api/applications/useCreateApplication";
import { Application } from "src/api/client-api/applications/useGetAllApplications";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "src/utils/useSnackbar";

export const ApplicationCreate = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { setSnackbarProps } = useSnackbar();
    const createApplicationMutation = useCreateApplication();
    const isCreating = createApplicationMutation.isLoading;

    const [formData, setFormData] = useState<Omit<Application, "id" | "createdAt" | "updatedAt">>({
        name: "",
        description: "",
        secretKey: "",
    });
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const [error, setError] = useState<string | null>(null);
    const [touched, setTouched] = useState<Record<string, boolean>>({});

    // Handle form input changes
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        // Mark field as touched
        if (!touched[name]) {
            setTouched((prev) => ({
                ...prev,
                [name]: true,
            }));
        }
    };

    // Validate form on touched fields change
    useEffect(() => {
        // Only validate touched fields
        const touchedErrors: Record<string, string> = {};

        Object.keys(touched).forEach((field) => {
            if (touched[field]) {
                if (field === "name" && !formData.name) {
                    touchedErrors.name = t("applications.nameRequired", "Name is required");
                }
                if (field === "description" && !formData.description) {
                    touchedErrors.description = t("applications.descriptionRequired", "Description is required");
                }
            }
        });

        setFormErrors(touchedErrors);
    }, [formData, touched, t]);

    // Validate all fields before submission
    const validateForm = (): boolean => {
        const errors: Record<string, string> = {};

        if (!formData.name) {
            errors.name = t("applications.nameRequired", "Name is required");
        }

        if (!formData.description) {
            errors.description = t("applications.descriptionRequired", "Description is required");
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Handle form submission
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        // Mark all fields as touched for validation
        const allTouched = Object.keys(formData).reduce(
            (acc, key) => {
                acc[key] = true;
                return acc;
            },
            {} as Record<string, boolean>,
        );

        setTouched(allTouched);

        if (!validateForm()) {
            return;
        }

        try {
            const response = await createApplicationMutation.mutateAsync({
                application: formData,
            });

            setSnackbarProps({
                message: t("applications.createSuccess", "Application created successfully"),
                type: "success",
            });

            // Navigate to the edit page
            navigate(URLHandler.settings.applications.edit(response.id));
        } catch (validationErrors: any) {
            console.error("Validation errors:", validationErrors);
            // If the error is a validation error object (from our API)
            if (validationErrors && typeof validationErrors === "object" && !validationErrors.message) {
                // These are field-specific validation errors
                setFormErrors(validationErrors);
            } else {
                // This is a general error
                setError(t("applications.createError", "Failed to create application"));
            }
        }
    };

    // Navigate back to applications list
    const handleBack = () => {
        navigate(URLHandler.settings.applications.list());
    };

    return (
        <Box sx={{ p: 4 }}>
            {/* Header */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <IconButton
                        onClick={handleBack}
                        sx={{ mr: 2 }}
                        aria-label={t("applications.back", "Back to applications list")}
                    >
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h4">{t("applications.createNew", "Create New Application")}</Typography>
                </Box>
            </Box>

            {/* Error message */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Application Form */}
            <Paper sx={{ p: 3 }}>
                <form onSubmit={handleSubmit} noValidate>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                name="name"
                                label={t("applications.name", "Application Name")}
                                value={formData.name}
                                onChange={handleInputChange}
                                onBlur={() => setTouched((prev) => ({ ...prev, name: true }))}
                                fullWidth
                                required
                                error={!!formErrors.name}
                                helperText={formErrors.name}
                                disabled={isCreating}
                                inputProps={{
                                    "aria-required": "true",
                                    "aria-invalid": !!formErrors.name,
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                name="description"
                                label={t("applications.description", "Description")}
                                value={formData.description}
                                onChange={handleInputChange}
                                onBlur={() => setTouched((prev) => ({ ...prev, description: true }))}
                                fullWidth
                                required
                                multiline
                                rows={3}
                                error={!!formErrors.description}
                                helperText={formErrors.description}
                                disabled={isCreating}
                                inputProps={{
                                    "aria-required": "true",
                                    "aria-invalid": !!formErrors.description,
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                                <Button variant="outlined" onClick={handleBack} sx={{ mr: 2 }} disabled={isCreating}>
                                    {t("applications.cancel", "Cancel")}
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    startIcon={isCreating ? <CircularProgress size={20} color="inherit" /> : <Save />}
                                    disabled={isCreating}
                                >
                                    {t("applications.create", "Create Application")}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </form>
            </Paper>
        </Box>
    );
};
